import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, OnInit, Output, signal } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { auditTime, forkJoin } from 'rxjs';
import { DynFormSection } from '../../../model/dyn-form/dyn-form-section.model';
import { SectionLayoutMetadata, SectionLayoutMetadataBase } from '../../../model/dyn-form/form-metadata/layout/section-layout.model';
import { FormView } from '../../../model/dyn-form/form-visualizer/formly-group.model';
import { EFormSection, EFormSectionModelValue, EFormSectionModelValueResponse, EFormSectionVisibility, FormValidationErrorController, StoreJob } from '../../../model/eform/eform-api.model';
import { ValidationErrorBase } from '../../../model/eform/scripts/validation-error.model';
import { ValidationSection } from '../../../model/eform/scripts/validation-section.model';
import { ProcedureInstanceBase } from '../../../model/procedure-instance/procedure-instance.model';
import { EFormDynamicSectionMetadataService } from '../../../services/eform/e-form-dynamic-section-metadata.service';
import { EFormValueService } from '../../../services/eform/e-form-value.service';
import { FormInstanceController } from '../../form-instance/form-instance-controller.model';
import { FormInstanceComponent } from '../../form-instance/form-instance.component';

let errorTypes: Map<string, string> = new Map()
errorTypes.set("VALIDATION-DEFINITION-MISMATCH", `El idenficadors de la secció i la metadata no coincideixen. Contacti amb el seu administrador`)
errorTypes.set("VALIDATION-HIDDEN-SECTION", `S'està intentar validar una secció amagada. Contacti amb el seu administrador`)
errorTypes.set("VALIDATION-NOT-VALID-SECTION", `Aquesta secció conté errors. Reviseu la secció abans de continuar.`)
errorTypes.set("VALIDATION-MALFORMED-SECTION-DEF", `La definició de la secció te un JSON malformat. Contacti amb el seu administrador `)
errorTypes.set("VALIDATION-MALFORMED-SECTION-VALUE", `El valor de la secció te un JSON malformat. Contacti amb el seu administrador`)
errorTypes.set("VALIDATION-NO-SECTION-DEF-FOUND", `No s'ha trobat la definició per aquesta secció. Contacti amb el seu administrador `)
errorTypes.set("VALIDATION-SECTION-DEF-MISMATCH", `La definició de la secció no correspon. Contacti amb el seu administrador `)


@Component({
    selector: 'app-dynamic-section',
    templateUrl: './dynamic-section.component.html',
    styleUrls: ['./dynamic-section.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatDividerModule, MatIconModule,
        FormInstanceComponent
    ]
})
export class DynamicSectionComponent implements EFormSection, OnInit, AfterViewInit, FormInstanceController {

    metadata = signal<SectionLayoutMetadata | null>(null)
    controller = signal<FormInstanceController>(this)
    formView = signal<FormView | null>(null)
    modelData = signal<any>(null)
    validForm = signal<boolean>(true)
    customData = signal<Map<string, any> | null>(null)

    @Output()
    visibilityChanges: EventEmitter<EFormSectionVisibility> = new EventEmitter()

    @Output()
    valueChanges: EventEmitter<StoreJob> = new EventEmitter()

    @Output()
    next: EventEmitter<boolean> = new EventEmitter()

    constructor(
        @Inject("CONTAINER_DATA") public data: {
            section: DynFormSection, procedureInstance: ProcedureInstanceBase, sectionErrors: FormValidationErrorController
        }, protected dynSectionMetadata: EFormDynamicSectionMetadataService, protected eFormValueService: EFormValueService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {
        let customData = new Map<string, any>()
        customData.set('dummy', false)
        customData.set('pi', data.procedureInstance)
        this.customData.set(customData)
    }
    ngAfterViewInit(): void {

        setTimeout(() => {
            this.validForm.set(false)
            if (this.formView()?.form.valid != null && this.formView()?.form.valid == true) {
                this.validForm.set(true)
            }
        }, 10)

    }
    sectionId(): number {
        return this.data.section.id
    }

    register(fv: FormView): void {

        this.formView.set(fv)
        this.formView()!.form.valueChanges.pipe(auditTime(1000)).subscribe(r => {

            this.validForm.set(false)

            if (this.formView()?.form.valid != null && this.formView()?.form.valid == true) {
                this.validForm.set(true)
            }

            let section: EFormSectionModelValue = {
                sectionId: this.data.section.id,
                value: r
            }

            let storeJob: StoreJob = { serviceInstanceId: this.data.procedureInstance.serviceInstanceId, procedureInstanceId: this.data.procedureInstance.id, value: section, validForm: this.validForm() }

            if (this.formView()?.form.pristine == true) {
                //nobody change the form, no change is required to be fired
                return
            }
            this.valueChanges.emit(storeJob)
        })


        if (this.modelData() != null) {
            this.formView()!.model = this.modelData()
        }
    }

    validate($event: Event): boolean {
        this.data.sectionErrors.clearErrors(this.data.section.id)
        this.formView()?.form.markAllAsTouched()
        $event.stopPropagation()
        this.next.emit(true)
        if (this.validForm() != null && this.validForm()) {
            return true
        }
        return false

    }

    ngOnInit(): void {

        forkJoin<[SectionLayoutMetadata, EFormSectionModelValueResponse]>([
            this.dynSectionMetadata.loadSectionMetadata(this.data.section.id),
            this.eFormValueService.getEFormSectionModelValue(this.data.procedureInstance.serviceInstanceId, this.data.procedureInstance.id, this.data.section.id)
        ]).subscribe(r => {

            if (r[1].ok == false) {
                //DO SOMETHING
                return
            }

            r[0].onFormCreation = false
            this.metadata.set(r[0])

            if (r[1].entity.value == null) {
                r[1].entity.value = SectionLayoutMetadataBase.getDefaultValue(this.metadata()!)
            }

            this.modelData.set(r[1].entity.value)

            this.changeDetectorRef.detectChanges()
        })

    }

    getErrorItemDesc(type: ValidationErrorBase): string {
        let err = errorTypes.get(type.errorCode)
        if (err) {
            return err
        }
        return type.errorMsg
    }

    serverSectionErrors(): ValidationSection[] {
        if (this.data.sectionErrors.sectionErrors) {
            return this.data.sectionErrors.sectionErrors.filter(p => p.sectionId == this.data.section.id && p.validationResult.ok == true && p.validationResult.entity.errors.length > 0)
        }
        return []
    }
}
